import React, { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Skeleton,
} from '@mui/material'
import {
  Refresh as RefreshIcon,
  Sync as SyncIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  Speed as SpeedIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material'
import { formatDistanceToNow } from 'date-fns'
import { tr } from 'date-fns/locale'

import ProviderCard from '../../components/Providers/ProviderCard'
import ProviderStatsCard from '../../components/Providers/ProviderStatsCard'
import PriceSyncDialog from '../../components/Providers/PriceSyncDialog'
import { useProviders } from '../../hooks/useProviders'
import { useProviderActions } from '../../hooks/useProviderActions'

const ProviderManagement = () => {
  const [syncDialog, setSyncDialog] = useState({ open: false, provider: null })
  const [confirmDialog, setConfirmDialog] = useState({ open: false, provider: null, action: null })

  const {
    data: providersData,
    isLoading,
    error,
    refetch,
  } = useProviders()

  const { 
    toggleProvider, 
    syncPrices, 
    isLoading: actionLoading 
  } = useProviderActions()

  const handleToggleProvider = async (provider) => {
    setConfirmDialog({
      open: true,
      provider,
      action: provider.is_active ? 'disable' : 'enable'
    })
  }

  const confirmToggle = async () => {
    try {
      await toggleProvider(confirmDialog.provider.id)
      refetch()
      setConfirmDialog({ open: false, provider: null, action: null })
    } catch (error) {
      console.error('Toggle provider error:', error)
    }
  }

  const handleSyncPrices = async (provider = null) => {
    if (provider) {
      setSyncDialog({ open: true, provider })
    } else {
      // Sync all providers
      try {
        await syncPrices()
        refetch()
      } catch (error) {
        console.error('Sync prices error:', error)
      }
    }
  }

  const getProviderStatusColor = (provider) => {
    if (!provider.is_active) return 'error'
    if (provider.price_count === 0) return 'warning'
    return 'success'
  }

  const getProviderStatusText = (provider) => {
    if (!provider.is_active) return 'Pasif'
    if (provider.price_count === 0) return 'Fiyat Yok'
    return 'Aktif'
  }

  const calculateTotalStats = () => {
    if (!providersData?.providers) return { active: 0, total: 0, totalPrices: 0, totalRentals: 0 }

    const providers = providersData.providers
    return {
      active: providers.filter(p => p.is_active).length,
      total: providers.length,
      totalPrices: providers.reduce((sum, p) => sum + parseInt(p.active_offerings || p.price_count || 0), 0),
      totalRentals: providers.reduce((sum, p) => sum + parseInt(p.rental_count || 0), 0),
    }
  }

  const stats = calculateTotalStats()

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Provider bilgileri yüklenirken hata oluştu: {error.message}
        </Alert>
      </Box>
    )
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
              Provider Yönetimi
            </Typography>
            <Typography variant="body1" color="text.secondary">
              SMS sağlayıcılarını yönetin ve fiyat senkronizasyonu yapın
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isLoading || actionLoading}
            >
              Yenile
            </Button>
            <Button
              variant="contained"
              startIcon={<SyncIcon />}
              onClick={() => handleSyncPrices()}
              disabled={isLoading || actionLoading}
              color="primary"
            >
              Tüm Fiyatları Senkronize Et
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <ProviderStatsCard
            title="Toplam Provider"
            value={stats.total}
            icon={<StorageIcon />}
            color="primary"
            loading={isLoading}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ProviderStatsCard
            title="Aktif Provider"
            value={stats.active}
            subtitle={`${stats.total - stats.active} pasif`}
            icon={<CheckCircleIcon />}
            color="success"
            loading={isLoading}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ProviderStatsCard
            title="Toplam Fiyat"
            value={stats.totalPrices}
            subtitle="ülke-servis kombinasyonu"
            icon={<SpeedIcon />}
            color="info"
            loading={isLoading}
          />
        </Grid>
        
        <Grid item xs={12} sm={6} md={3}>
          <ProviderStatsCard
            title="Toplam Kiralama"
            value={stats.totalRentals}
            subtitle="tüm zamanlar"
            icon={<TrendingUpIcon />}
            color="warning"
            loading={isLoading}
          />
        </Grid>
      </Grid>

      {/* System Status */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Sistem Durumu
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isLoading ? (
                  <Skeleton variant="circular" width={24} height={24} />
                ) : (
                  <CheckCircleIcon color={providersData?.summary?.lastSyncStatus === 'success' ? 'success' : 'warning'} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Fiyat Senkronizasyonu
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isLoading ? (
                      <Skeleton variant="text" width={120} />
                    ) : (
                      providersData?.summary?.lastSyncAt
                        ? `Son guncelleme: ${formatDistanceToNow(new Date(providersData.summary.lastSyncAt), { addSuffix: true, locale: tr })}`
                        : '-'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isLoading ? (
                  <Skeleton variant="circular" width={24} height={24} />
                ) : (
                  <CheckCircleIcon color={stats.active === stats.total ? 'success' : 'warning'} />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    API Baglantilari
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isLoading ? (
                      <Skeleton variant="text" width={150} />
                    ) : (
                      stats.active === stats.total
                        ? 'Tum provider\'lar erisilebilir'
                        : `${stats.active}/${stats.total} provider aktif`
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {isLoading ? (
                  <Skeleton variant="circular" width={24} height={24} />
                ) : (
                  providersData?.summary?.lowStockCountries > 0
                    ? <WarningIcon color="warning" />
                    : <CheckCircleIcon color="success" />
                )}
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Stok Durumu
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {isLoading ? (
                      <Skeleton variant="text" width={120} />
                    ) : (
                      providersData?.summary?.lowStockCountries != null
                        ? providersData.summary.lowStockCountries > 0
                          ? `${providersData.summary.lowStockCountries} ulkede dusuk stok`
                          : 'Stoklar yeterli'
                        : '-'
                    )}
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Providers Grid */}
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Provider Listesi
      </Typography>
      
      {isLoading ? (
        <Box sx={{ mb: 2 }}>
          <LinearProgress />
        </Box>
      ) : null}

      <Grid container spacing={3}>
        {providersData?.providers?.map((provider) => (
          <Grid item xs={12} md={6} lg={4} key={provider.id}>
            <ProviderCard
              provider={provider}
              onToggle={() => handleToggleProvider(provider)}
              onSync={() => handleSyncPrices(provider)}
              loading={actionLoading}
              statusColor={getProviderStatusColor(provider)}
              statusText={getProviderStatusText(provider)}
            />
          </Grid>
        ))}
      </Grid>

      {/* Confirm Toggle Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ open: false, provider: null, action: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Provider Durumunu Değiştir
        </DialogTitle>
        <DialogContent>
          <Typography>
            <strong>{confirmDialog.provider?.name}</strong> provider'ını{' '}
            {confirmDialog.action === 'enable' ? 'aktifleştirmek' : 'pasifleştirmek'} istediğinizden emin misiniz?
          </Typography>
          
          {confirmDialog.action === 'disable' && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Bu provider pasifleştirildiğinde, yeni kiralamalar için kullanılamayacaktır.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialog({ open: false, provider: null, action: null })}
            disabled={actionLoading}
          >
            İptal
          </Button>
          <Button 
            onClick={confirmToggle}
            variant="contained"
            disabled={actionLoading}
            color={confirmDialog.action === 'disable' ? 'warning' : 'primary'}
          >
            {actionLoading ? 'İşleniyor...' : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Price Sync Dialog */}
      <PriceSyncDialog
        open={syncDialog.open}
        provider={syncDialog.provider}
        onClose={() => setSyncDialog({ open: false, provider: null })}
        onSuccess={() => {
          refetch()
          setSyncDialog({ open: false, provider: null })
        }}
      />
    </Box>
  )
}

export default ProviderManagement